import { useEffect, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';

const resetStoryCallable = httpsCallable<{ storyId: string }, { ok: boolean }>(functions, 'resetStory');

// Lightweight veil shown while the next scene generates. No summary, no gate — it
// auto-dismisses straight into the new scene when generation completes. If
// generation stalls, an escape hatch appears so the run is never stranded.
export function SceneTurnOverlay({ storyId }: { storyId: string }) {
  const [stalled, setStalled] = useState(false);
  const [restarting, setRestarting] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setStalled(true), 45_000);
    return () => clearTimeout(t);
  }, []);

  async function restart() {
    if (restarting) return;
    setRestarting(true);
    try {
      await resetStoryCallable({ storyId });
    } catch {
      setRestarting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-slate-950/85 backdrop-blur-sm">
      <div className="text-center space-y-5 px-8">
        <div className="flex justify-center gap-2" aria-hidden>
          <span className="w-2.5 h-2.5 rounded-full bg-amber-300/80 animate-bounce [animation-delay:-0.3s]" />
          <span className="w-2.5 h-2.5 rounded-full bg-amber-300/80 animate-bounce [animation-delay:-0.15s]" />
          <span className="w-2.5 h-2.5 rounded-full bg-amber-300/80 animate-bounce" />
        </div>
        <p className="text-2xl text-slate-200 tracking-wide">the story turns…</p>
        {stalled && (
          <div className="pt-2 space-y-2">
            <p className="text-xs text-slate-500">This is taking longer than usual.</p>
            <button
              type="button"
              onClick={restart}
              disabled={restarting}
              className="rounded-lg border border-slate-600 text-slate-300 hover:text-slate-100 px-4 py-1.5 text-sm disabled:opacity-50"
            >
              {restarting ? 'restarting…' : '↺ restart the run'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
